import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import ActivityLevelChart from "../../components/ActivityLevelChart";
import ActivityWeightChart from "../../components/ActivityWeightChart";
import InfoTip from "../../components/InfoTip";
import ViewActivity from "../../components/ViewActivity";
import {
  ActivityCycleMetricIcon,
  ActivityMetricInfoIcon,
  ActivityWeightMetricIcon,
} from "../../components/ActivityDetailMetricIcons";
import { elapsedTimeContainer } from "../../components/formatted/FormattedDateAndTime";
import { formatMass } from "../../components/utils/formatMass";
import {
  EmptyStar,
  FilledStar,
} from "../../ui/components/SortingQualityFooter";
import { ActivityDetailSection } from "./components/ActivityDetailSections";
import { ActivityDetailRecommendationsSection } from "./components/ActivityDetailRecommendationsSection";
import { ActivitySummaryKeyMoments } from "./components/ActivitySummaryKeyMoments";
import { SummaryMetric } from "./components/ActivityDetailSummaryMetric";
import {
  buildActivitySummaryKeyMoments,
  getCollectionLevel,
  getCollectionMassKg,
  getContaminantRate,
  getFillingKeyMoments,
} from "./utils/activityDetailAnalysis";
import {
  filterRecommendationsForDisplay,
  hasFillingTimeline,
} from "./utils/activityDetailDisplayRules";
import ActivityDetailStatisticsTab from "./ActivityDetailStatisticsTab";
import ActivityCompactionTab from "./ActivityCompactionTab";
export { ActivityDetailFollowUpTab } from "./components/ActivityDetailFollowUpTab";

const FlowQualityInfoTip = ({ t }) => (
  <InfoTip widthMaxRem={16} icon={<ActivityMetricInfoIcon />}>
    <p className="m-0 text-[13px] leading-tight opacity-90">
      {t(
        "Assessment of material quality based on the volumetric proportion of detected errors",
      )}
      .
    </p>
    <ul className="mt-1 space-y-0.5 text-[13px] leading-none">
      <li className="flex items-center gap-1.5">
        <span className="whitespace-nowrap">
          <i className="far fa-star text-[13px] align-middle"></i>
          <i className="far fa-star text-[13px] ml-0.5"></i>
          <i className="far fa-star text-[13px] ml-0.5 align-middle"></i>
        </span>
        <span className="text-[13px] opacity-90 whitespace-nowrap">
          {t("More than 30% of contaminants")}
        </span>
      </li>
      <li className="flex items-center gap-1.5">
        <span className="whitespace-nowrap">
          <i className="fas fa-star text-[13px] align-middle"></i>
          <i className="far fa-star text-[13px] ml-0.5 align-middle"></i>
          <i className="far fa-star text-[13px] ml-0.5 align-middle"></i>
        </span>
        <span className="text-[13px] opacity-90 whitespace-nowrap">
          {t("Between 15% and 30% of contaminants")}
        </span>
      </li>
      <li className="flex items-center gap-1.5">
        <span className="whitespace-nowrap">
          <i className="fas fa-star text-[13px] align-middle"></i>
          <i className="fas fa-star text-[13px] ml-0.5 align-middle"></i>
          <i className="far fa-star text-[13px] ml-0.5 align-middle"></i>
        </span>
        <span className="text-[13px] opacity-90 whitespace-nowrap">
          {t("Between 5% and 15% of contaminants")}
        </span>
      </li>
      <li className="flex items-center gap-1.5">
        <span className="whitespace-nowrap">
          <i className="fas fa-star text-[13px] align-middle"></i>
          <i className="fas fa-star text-[13px] ml-0.5 align-middle"></i>
          <i className="fas fa-star text-[13px] ml-0.5 align-middle"></i>
        </span>
        <span className="text-[13px] opacity-90 whitespace-nowrap">
          {t("Less than 5% of contaminants")}
        </span>
      </li>
    </ul>
  </InfoTip>
);

const SummaryMetricStars = ({ numberOfStars, size = 20 }) => (
  <div className="flex shrink-0 items-center gap-0.5">
    {[1, 2, 3].map((star) =>
      star <= (numberOfStars ?? 0) ? (
        <FilledStar key={star} size={size} />
      ) : (
        <EmptyStar key={star} size={size} />
      ),
    )}
  </div>
);

export const ActivityDetailSummaryTab = ({
  data,
  historyCycle,
  clientRegisteredWeight,
  numberOfStars,
  totalVolumeSortingError,
  totalWasteVolume,
  recommendations,
  activityProps,
}) => {
  const { t, i18n } = useTranslation();
  const [isSummaryOpen, setIsSummaryOpen] = useState(true);
  const collectionLevel = getCollectionLevel(historyCycle);
  const contaminantRate = getContaminantRate(
    totalVolumeSortingError,
    totalWasteVolume,
  );
  const hasRegisteredWeight = Boolean(clientRegisteredWeight);
  const registeredMassKg = hasRegisteredWeight
    ? getCollectionMassKg(historyCycle, clientRegisteredWeight)
    : null;
  const cycleDuration =
    historyCycle?.length > 0
      ? elapsedTimeContainer(
          historyCycle[historyCycle.length - 1]._time,
          i18n.language,
          false,
          new Date(data._time),
        )
      : "-";
  const keyMoments = useMemo(
    () => buildActivitySummaryKeyMoments(historyCycle),
    [historyCycle],
  );
  const findHistoryId = activityProps?.findHistoryId;
  const historyId = activityProps?.historyId;
  const selectedReadingTime =
    historyCycle?.[historyId]?._time ?? historyCycle?.[0]?._time ?? null;
  const showFillingTimeline = hasFillingTimeline(historyCycle);
  const displayedRecommendations = useMemo(
    () => filterRecommendationsForDisplay(recommendations, historyCycle),
    [historyCycle, recommendations],
  );

  return (
    <div className="flex flex-col gap-6 w-full">
      <section className="flex flex-col gap-3 w-full min-w-0">
        <button
          type="button"
          onClick={() => setIsSummaryOpen((open) => !open)}
          className="char-section-header char-section-header--general-info flex flex-row !justify-start items-center gap-3 !mb-0 bg-transparent border-0 p-0 cursor-pointer text-left w-fit max-w-full"
          aria-expanded={isSummaryOpen}
        >
          <h2 className="char-section-header-title !mb-0">{t("Summary")}</h2>
          <i
            className={`fas fa-chevron-${isSummaryOpen ? "up" : "down"} text-xl text-[#6B7280] leading-none`}
            aria-hidden
          />
        </button>

        {isSummaryOpen ? (
          <div className="flex flex-col gap-6 w-full">
            <div
              className={`grid gap-3 ${
                showFillingTimeline
                  ? "grid-cols-4 md:grid-cols-2 sm:grid-cols-1"
                  : "grid-cols-2 md:grid-cols-2 sm:grid-cols-1"
              }`}
            >
              {showFillingTimeline ? (
                <SummaryMetric
                  label={t("Level at collection")}
                  value={
                    collectionLevel == null
                      ? "-"
                      : `${Math.round(collectionLevel)}%`
                  }
                />
              ) : null}
              <SummaryMetric
                label={t("Weight")}
                value={
                  hasRegisteredWeight
                    ? formatMass(registeredMassKg) || "-"
                    : t("pleaseEnterWeight")
                }
                detail={
                  hasRegisteredWeight ? t("Registered weight") : undefined
                }
                inlineDetail={hasRegisteredWeight}
                icon={<ActivityWeightMetricIcon />}
              />
              <SummaryMetric
                label={t("Waste category quality")}
                infoTip={<FlowQualityInfoTip t={t} />}
                value={
                  contaminantRate == null ? (
                    "-"
                  ) : (
                    <div className="flex items-center gap-2 min-w-0">
                      <SummaryMetricStars numberOfStars={numberOfStars} />
                      <span className="shrink-0 leading-none">-</span>
                      <span className="flex min-w-0 items-center gap-1 leading-none">
                        <span className="shrink-0">{contaminantRate}%</span>
                        <span className="min-w-0 truncate">
                          {t("of undesirables")}
                        </span>
                      </span>
                    </div>
                  )
                }
              />
              {showFillingTimeline ? (
                <SummaryMetric
                  label={t("Cycle duration")}
                  value={cycleDuration}
                  icon={<ActivityCycleMetricIcon />}
                />
              ) : null}
            </div>
            <ActivityDetailRecommendationsSection
              recommendations={displayedRecommendations}
            />
          </div>
        ) : null}
      </section>

      {showFillingTimeline ? (
        <div className="grid grid-cols-3 md:grid-cols-1 gap-4 items-stretch w-full min-w-0">
          <section className="col-span-2 md:col-span-1 flex flex-col min-w-0">
            <div className="char-section-header char-section-header--general-info !mb-3 min-w-0">
              <h2 className="char-section-header-title !mb-0">
                {t("Filling level")}
              </h2>
            </div>
            <article className="bg-white border border-gray-300 rounded-xl p-4 flex flex-col min-w-0 flex-1">
              <ActivityLevelChart
                container={data}
                historyCycle={historyCycle}
                findHistoryId={findHistoryId}
                showPackmat={false}
                showLimitFillingLevel={false}
                enableHudInteraction
                selectedReadingTime={selectedReadingTime}
              />
            </article>
          </section>
          <section className="col-span-1 md:col-span-1 flex flex-col min-h-[280px] md:min-h-[240px] min-w-0">
            <ActivitySummaryKeyMoments
              moments={keyMoments}
              selectedTime={selectedReadingTime}
              onSelectMoment={findHistoryId}
            />
          </section>
        </div>
      ) : null}

      {activityProps ? (
        <ViewActivity
          {...activityProps}
          useDetailHeaderLayout
          showDetailMetrics={false}
          showDistributionCharts={false}
          showCompactWeightChart={false}
          selectedReadingTime={selectedReadingTime}
        />
      ) : null}
    </div>
  );
};

const FillingKeyMoments = ({ moments }) => {
  const { t, i18n } = useTranslation();
  const language = i18n.language || "fr-FR";
  const formatDate = (time) =>
    new Date(time).toLocaleDateString(language, {
      day: "numeric",
      month: "long",
      timeZone: "Europe/Paris",
    });
  const formatTime = (time) =>
    new Date(time).toLocaleTimeString(language, {
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "Europe/Paris",
    });
  const items = [
    ...moments.productionDays.map((day) => ({
      key: `production-${day.dayKey}`,
      icon: "fas fa-industry",
      text: t("activityDetailProductionDayMoment", {
        date: formatDate(day.peakTime),
        gain: day.gain,
        time: formatTime(day.peakTime),
      }),
    })),
    ...moments.significantCompactions.map((compaction) => ({
      key: `compaction-${compaction.time}`,
      icon: "fas fa-compress-arrows-alt",
      text: t("activityDetailCompactionMoment", {
        date: formatDate(compaction.time),
        time: formatTime(compaction.time),
        gain: compaction.gain,
      }),
    })),
    ...(moments.collection
      ? [
          {
            key: "collection",
            icon: "fas fa-truck",
            text: t(
              `activityDetailCollection_${moments.collection.status}`,
              moments.collection,
            ),
          },
        ]
      : []),
  ];

  if (items.length === 0) {
    return (
      <p className="m-0 p-4 text-sm text-[#6B7280]">
        {t("activityDetailNoKeyMoments")}
      </p>
    );
  }

  return (
    <ul className="m-0 p-4 flex flex-col gap-3 list-none">
      {items.map((item) => (
        <li
          key={item.key}
          className="flex items-start gap-3 rounded-lg bg-[#F8F8F8] p-3"
        >
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#E9FBFB] text-[#00A7A6]">
            <i className={item.icon} aria-hidden />
          </span>
          <span className="text-sm leading-6 text-[#222222]">{item.text}</span>
        </li>
      ))}
    </ul>
  );
};

export const ActivityDetailFillingTab = ({
  data,
  historyCycle,
  findHistoryId,
  numberOfPackmat,
  onAfterUpdate,
}) => {
  const { t } = useTranslation();
  const keyMoments = useMemo(
    () => getFillingKeyMoments(historyCycle, data?.limitFillingLevel),
    [data?.limitFillingLevel, historyCycle],
  );
  const showCompaction = Boolean(data?.packmatActive);

  return (
    <div className="flex flex-col gap-6 w-full">
      <div className="grid grid-cols-2 md:grid-cols-1 gap-6">
        <ActivityDetailSection title={t("Filling level")}>
          {historyCycle?.length > 1 ? (
            <ActivityLevelChart
              container={data}
              historyCycle={historyCycle}
              findHistoryId={findHistoryId}
            />
          ) : (
            <p className="m-0 p-4 text-sm text-[#6B7280]">
              {t("activityDetailInsufficientChartData")}
            </p>
          )}
        </ActivityDetailSection>
        <ActivityDetailSection title={t("Key moments")}>
          <FillingKeyMoments moments={keyMoments} />
        </ActivityDetailSection>
      </div>
      <ActivityDetailSection title={t("Weight")}>
        {historyCycle?.length > 1 ? (
          <ActivityWeightChart
            container={data}
            historyCycle={historyCycle}
            findHistoryId={findHistoryId}
          />
        ) : (
          <p className="m-0 p-4 text-sm text-[#6B7280]">
            {t("activityDetailInsufficientChartData")}
          </p>
        )}
      </ActivityDetailSection>
      {showCompaction ? (
        <ActivityCompactionTab
          container={data}
          historyCycle={historyCycle}
          numberOfPackmat={numberOfPackmat}
          onAfterUpdate={onAfterUpdate}
        />
      ) : null}
    </div>
  );
};

export const ActivityDetailSortingTab = ({ statisticsProps }) => (
  <div className="flex flex-col gap-6 w-full">
    <ActivityDetailStatisticsTab {...statisticsProps} showWeightChart={false} />
  </div>
);
